import { getFile } from '@/services/file';
import { useEffect, useState } from 'react';

interface UseImageUrlProps {
  filePath: string | null | undefined;
  enabled?: boolean;
  autoLoad?: boolean;
}

interface UseImageUrlReturn {
  imgUrl: string | null;
  loading: boolean;
  error: string | null;
  loadImage: () => Promise<void>;
  clearImage: () => void;
}

export const useImageUrl = ({
  filePath,
  enabled = true,
  autoLoad = true,
}: UseImageUrlProps): UseImageUrlReturn => {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadImage = async () => {
    if (!filePath || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getFile(filePath);

      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        setImgUrl(url);
      } else if (response && response.success && response.data) {
        let blob;
        if (response.data instanceof Blob) {
          blob = response.data;
        } else {
          blob = new Blob([response.data]);
        }
        const url = window.URL.createObjectURL(blob);
        setImgUrl(url);
      } else {
        setError('Không thể tải ảnh');
      }
    } catch (err) {
      setError('Lỗi khi tải ảnh');
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    if (imgUrl) {
      window.URL.revokeObjectURL(imgUrl);
    }
    setImgUrl(null);
    setError(null);
  };

  useEffect(() => {
    if (autoLoad && filePath && enabled) {
      loadImage();
    } else if (!filePath) {
      clearImage();
    }

    return () => {
      if (imgUrl) {
        window.URL.revokeObjectURL(imgUrl);
      }
    };
  }, [filePath, enabled, autoLoad]); // Chỉ depend vào các giá trị primitive

  return {
    imgUrl,
    loading,
    error,
    loadImage,
    clearImage,
  };
};
